import { studyList } from "@ir/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { formatAccessSummary } from "@/features/studies/accessSummary";
import { apiFetch } from "@/lib/api";
import { canManageStudies } from "@/lib/roles";
import { authClient } from "@/lib/authClient";

export const Route = createFileRoute("/_auth/admin/estudios/")({
  async beforeLoad() {
    const { data } = await authClient.getSession();
    if (!canManageStudies(data?.user.role)) redirect({ to: "/insights", throw: true });
  },
  component: AdminStudiesPage,
});

const STATUS_TONE = { draft: "neutral", published: "ok", archived: "warn" } as const;

// Exportada (no solo usada por `Route.component`) por si una prueba futura
// necesita renderizarla directo, igual que hacían las pantallas de admin
// anteriores a esta tarea.
export function AdminStudiesPage() {
  const { t } = useTranslation();

  const studies = useQuery({
    queryKey: ["studies", "admin"],
    queryFn: () => apiFetch("/api/studies?pageSize=100", { schema: studyList }),
  });

  return (
    <>
      <PageHeader
        title={t("admin.studies")}
        actions={
          <Button asChild>
            <Link to="/admin/estudios/nuevo">{t("admin.newStudy")}</Link>
          </Button>
        }
      />

      <div className="px-8 py-6">
        <div className="overflow-x-auto rounded-[3px] border border-line bg-card">
          {studies.isPending && (
            <p className="p-4 text-nota text-ink3">{t("common.loading")}</p>
          )}

          {studies.data && (
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-line text-left text-micro text-ink3">
                  <th className="px-3 py-2 font-bold">{t("studyForm.title")}</th>
                  <th className="px-3 py-2 font-bold">{t("admin.status")}</th>
                  <th className="px-3 py-2 font-bold">{t("studyForm.organizations")}</th>
                  <th className="px-3 py-2 font-bold">{t("studyForm.html")}</th>
                  <th className="px-3 py-2 text-right font-bold">{t("admin.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {studies.data.items.map((study) => (
                  <tr key={study.id} className="border-b border-line2 hover:bg-bur-xl">
                    <td className="px-3 py-2.5">
                      <span className="block font-bold text-ink">{study.title}</span>
                      <span className="block text-micro text-ink3">{study.slug}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge tone={STATUS_TONE[study.status]}>
                        {t(`admin.${study.status}`)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-micro text-ink3">
                      {formatAccessSummary(study.organizations, study.hasUserAccess, t)}
                    </td>
                    <td className="px-3 py-2.5 text-ink2">
                      {study.hasContent ? t("studyForm.htmlLoaded") : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Button asChild variant="ghost" size="sm">
                        <Link to="/admin/estudios/$id" params={{ id: study.id }}>
                          {t("common.edit")}
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
